import * as THREE from "three";
import { getIntersectPoint } from "./core";
import { colors } from "./colors";

const GIZMO_COLORS = {
  x: new THREE.Color(0xff4d4d), //RED
  y: new THREE.Color(0x4dff4d), //GREEN
  z: new THREE.Color(0x4d4dff), //BLUE
};

export class Gizmo {
  constructor(scene, camera, raycaster, renderer) {
    this.scene = scene;
    this.camera = camera;
    this.raycaster = raycaster;
    this.renderer = renderer;
    this.gizmoGroup = null;
    this.targetObject = null;
    this.layer = 1;
    this.isDragging = false;
    this.movingAxis = null;
    this.isMoving = false;
    this.initialPosition = null;
    this.isScaling = false;
    this.scalingAxis = null;
  }

  startDragging(event, object) {
    this.isDragging = true;
    if (this.isMoveHandle(object)) {
      this.startMoving(event, object);
    } else {
      this.startScaling(event, object);
    }
  }

  handleDragging(event) {
    if (this.isMoving) {
      this.handleMoving(event);
    } else if (this.isScaling) {
      this.handleScaling(event);
    }
  }

  stopDragging() {
    this.isDragging = false;
    this.isMoving = false;
    this.isScaling = false;
    this.movingAxis = null;
  }

  startMoving(event, object) {
    this.isMoving = true;
    this.movingAxis = object.userData.axis;

    this.initialPosition = this.targetObject.position.clone();
    this.initialIntersection = getIntersectPoint(
      event,
      this.camera,
      this.raycaster,
      this.renderer,
      this.movingAxis,
    );
  }

  handleMoving(event) {
    const currentIntersection = getIntersectPoint(
      event,
      this.camera,
      this.raycaster,
      this.renderer,
      this.movingAxis,
    );
    if (!currentIntersection) return;

    const delta = currentIntersection.clone().sub(this.initialIntersection);

    let axisVector = new THREE.Vector3();
    switch (this.movingAxis) {
      case "x":
        axisVector.set(1, 0, 0);
        break;
      case "y":
        axisVector.set(0, 1, 0);
        break;
      case "z":
        axisVector.set(0, 0, 1);
        break;
    }

    // Project delta onto the axis
    const scalar = delta.dot(axisVector);
    const movement = axisVector.clone().multiplyScalar(scalar);

    const newPos = this.initialPosition.clone().add(movement);
    this.targetObject.position.copy(newPos);
  }

  startScaling(event, object) {
    this.isScaling = true;
    this.scalingAxis = object.userData.axis;

    this.startScale = this.targetObject.scale.clone();

    this.initialIntersection = getIntersectPoint(
      event,
      this.camera,
      this.raycaster,
      this.renderer,
      this.scalingAxis,
    );
  }

  handleScaling(event) {
    if (!this.targetObject || !this.isScaling) return;

    const currentIntersection = getIntersectPoint(
      event,
      this.camera,
      this.raycaster,
      this.renderer,
      this.scalingAxis,
    );
    if (!currentIntersection) return;

    const delta = currentIntersection.clone().sub(this.initialIntersection);

    let axisVector = new THREE.Vector3();
    switch (this.scalingAxis) {
      case "x":
        axisVector.set(1, 0, 0);
        break;
      case "y":
        axisVector.set(0, 1, 0);
        break;
      case "z":
        axisVector.set(0, 0, 1);
        break;
    }

    const scalar = delta.dot(axisVector); // how much we moved along the axis

    // Sensitivity and minimum scale
    const sensitivity = 1.0; // You can reduce this to make it slower
    const scaleChange = scalar * sensitivity;

    const newScale = this.startScale.clone();

    newScale[this.scalingAxis] = Math.max(
      0.01,
      this.startScale[this.scalingAxis] + scaleChange,
    );

    this.targetObject.scale.copy(newScale);
  }

  isGizmo(object) {
    return object.userData.type === "move" || object.userData.type === "scale";
  }

  isMoveHandle(object) {
    return object.userData.type === "move";
  }

  isScaleHandle(object) {
    return object.userData.type === "scale";
  }

  add(targetObject) {
    this.remove();

    this.targetObject = targetObject;
    this.gizmoGroup = new THREE.Group();

    const axes = ["x", "y", "z"];
    const directions = {
      x: new THREE.Vector3(1, 0, 0),
      y: new THREE.Vector3(0, 1, 0),
      z: new THREE.Vector3(0, 0, 1),
    };

    axes.forEach((axis) => {
      const color = GIZMO_COLORS[axis];
      const dir = directions[axis];

      const material = new THREE.MeshStandardMaterial({
        color,
        transparent: true,
        opacity: 0.8,
        emissive: color.clone().multiplyScalar(0.3),
        metalness: 0.6,
        roughness: 0.2,
        depthTest: false,
      });

      // LINE
      const line = new THREE.Mesh(
        new THREE.CylinderGeometry(0.055, 0.055, 2, 32),
        material.clone(),
      );
      line.userData = { type: "move", axis };

      if (axis === "x") line.rotation.z = -Math.PI / 2;
      if (axis === "z") line.rotation.x = Math.PI / 2;
      line.position.copy(dir.clone().multiplyScalar(1));

      // CONE
      const coneMaterial = material.clone();
      coneMaterial.opacity = 1; // Lower opacity for cone
      coneMaterial.depthTest = false;

      const cone = new THREE.Mesh(
        new THREE.ConeGeometry(0.11, 0.3, 32), // radius increased to match thicker line
        coneMaterial,
      );
      cone.userData = { type: "move", axis };
      if (axis === "x") cone.rotation.z = -Math.PI / 2;
      if (axis === "z") cone.rotation.x = Math.PI / 2;
      cone.position.copy(dir.clone().multiplyScalar(2));

      // LINE AND CONE
      const axisGroup = new THREE.Group();
      axisGroup.userData = { type: "move", axis };
      axisGroup.layers.set(this.layer);

      line.layers.set(this.layer);
      cone.layers.set(this.layer);
      axisGroup.add(line, cone);

      // SCALE
      const sphere = new THREE.Mesh(
        new THREE.SphereGeometry(0.12, 24, 24),
        material.clone(),
      );
      sphere.userData = { type: "scale", axis };
      sphere.position.copy(dir.clone().multiplyScalar(2.4));
      sphere.layers.set(this.layer);

      this.gizmoGroup.add(axisGroup, sphere);
    });

    this.scene.add(this.gizmoGroup);
    this.update();
  }

  update() {
    if (!this.gizmoGroup || !this.targetObject) return;

    this.gizmoGroup.position.copy(this.targetObject.position);
    this.gizmoGroup.quaternion.copy(this.targetObject.quaternion);
  }

  remove() {
    if (this.gizmoGroup) {
      this.scene.remove(this.gizmoGroup);
      this.gizmoGroup.traverse((child) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) child.material.dispose();
      });
      this.gizmoGroup = null;
      this.targetObject = null;
    }
  }

  removeHighlight() {
    if (!this.gizmoGroup) return;

    this.gizmoGroup.traverse((child) => {
      if (
        !child.isMesh ||
        !child.material ||
        !GIZMO_COLORS[child.userData.axis]
      )
        return;

      const baseColor = GIZMO_COLORS[child.userData.axis];
      child.material.color.copy(baseColor);
      child.material.emissive = baseColor.clone().multiplyScalar(0.3);
      child.material.needsUpdate = true;
    });
  }

  highlightHandle(object) {
    const highlightColor = new THREE.Color(colors["indigo300"]);
    const emissiveColor = highlightColor.clone().multiplyScalar(0.6);

    const updateMaterial = (mesh) => {
      mesh.material.color.copy(highlightColor);
      mesh.material.emissive.copy(emissiveColor);
    };

    const isArrow = object.userData.type === "move";
    if (isArrow) {
      object.parent.children.forEach((child) => {
        updateMaterial(child);
      });
    } else {
      updateMaterial(object);
    }
  }
}
